import * as React from "react";
import {useCallback, useState} from "react";
import {Col, Row} from "react-bootstrap";
import {
    getCompatibleLocales,
    getPossibleStates,
    isAcquiring,
    Locale,
    Session,
    SessionFinalStates,
    SessionSimpleState,
    SessionState,
    SessionStateMap,
    SimpleSessionStateMap
} from "../types/sessionsstate";
import {Routes} from "../utils/router";
import {
    createLinkToDownload,
    formatNumber,
    loadFromLocalStorage,
    transformDataParams,
    tryCall
} from "../utils/commons.ts";
import {
    confirmBox,
    error,
    KCard,
    KColumnAction,
    KModal,
    KModalBackdrop,
    KModalSize,
    KModalVariant,
    KTableColumnType,
    KTableFixedSelectionFilter,
    KTableFreeTextFilter,
    KTableGlobalActionType,
    KTableLoader,
    KTableSortingDirection,
    Translate,
    useKTable
} from "@kopjra/uikit";
import moment from "moment/moment";
import {
    useAbortSessionMutation,
    useCreateSessionMutation,
    useDeleteSessionMutation,
    useGetArchiveLinkMutation,
    useGetSessionsQuery,
    useLazyGetReportUrlQuery
} from "../api/session.ts";
import {useTranslation} from "react-i18next";
import {useAppDispatch} from "../utils/store.ts";
import {DangerHtml} from "./DangerHtml.tsx";
import {setReferrerRoute} from "../slices/global.ts";

export type Props = {
    reduced?: boolean;
};

export const Acquisitions: React.FC<Props> = ({reduced = false}) => {
    const [forceHide, setForceHide] = useState(false);

    const tableName = `sessions/list/${reduced ? "reduced" : "full"}`;

    const {Table: SessionTable, dataParams: sessionDataParams} = useKTable<Session>({id: tableName, initialDataParams: {
        sort: {field: "updated", direction: KTableSortingDirection.DESC},
        filters: reduced ? {"state": getPossibleStates([SessionSimpleState.FAILED], true).join(",")} : {},
    }});
    const {data: sessions, refetch} = useGetSessionsQuery(transformDataParams(sessionDataParams), {pollingInterval: 10000});
    const {Table: StartedSessionsTable, dataParams: startedSessionDataParams} = useKTable<Session>({id: "startedSessionsTable", initialDataParams: {
        filters: {"state": SessionState.STARTED},
        skip: 0,
        top: 10,
    }});
    const {data: statedSessions} = useGetSessionsQuery(transformDataParams(startedSessionDataParams), {pollingInterval: 5000});

    const [triggerAbortSession] = useAbortSessionMutation();
    const [triggerCreateSession] = useCreateSessionMutation();
    const [triggerGetArchiveLink] = useGetArchiveLinkMutation();
    const [triggerGetReportUrl] = useLazyGetReportUrlQuery();
    const [triggerDeleteSession] = useDeleteSessionMutation();

    const dispatch = useAppDispatch();
    const onSetSession = useCallback((s: Session) => {
        Routes.goNavigate(`${s.id}`);
    }, [dispatch]);

    const {t} = useTranslation();

    const actions: KColumnAction<Session>[] = [
        {
            label: <><i className="fal fa-arrow-right-to-bracket action-icon"/> <Translate i18nKey="acquisitions.table.actions.enter"/></>,
            handler: async (datum) => onSetSession(datum),
            visible: (datum) => isAcquiring(datum) && !!datum.frameUrl,
            tooltip: <Translate i18nKey="acquisitions.table.actions.entertip"/>,
        },
        {
            label: <><i className="fal fa-eye action-icon"/> <Translate i18nKey="acquisitions.table.actions.view"/></>,
            handler: async (datum: Session) => {dispatch(setReferrerRoute(reduced ? Routes.routeSessions : Routes.routeMy_sessions)); Routes.goA_session(`${datum.id}`)},
            visible: (datum: Session) => SessionStateMap[datum.state] === SessionSimpleState.COMPLETED,
            tooltip: <Translate i18nKey="acquisitions.table.actions.viewtip"/>,
        },
        {
            label: <><i className="fal fa-times action-icon"/> <Translate i18nKey="acquisitions.table.actions.abort"/></>,
            handler: async (datum: Session) => {
                await tryCall(triggerAbortSession(datum.id).unwrap(), {error: t("error.aborting")});
            },
            confirmation: true,
            visible: (datum: Session) => SessionStateMap[datum.state] === SessionSimpleState.INITIALIZING || SessionStateMap[datum.state] === SessionSimpleState.READY || (SessionStateMap[datum.state] === SessionSimpleState.STARTED && datum.consumedCredits <= 1),
            tooltip: <Translate i18nKey="acquisitions.table.actions.aborttip"/>,
        },
        {
            label: <><i className="fal fa-redo action-icon"/> <Translate i18nKey="acquisitions.table.actions.retry"/></>,
            handler: async (datum: Session) => {
                await tryCall(triggerCreateSession({
                    sessionInput: {
                        name: datum.name,
                        fullProxy: datum.fullProxy,
                    },
                    customization: loadFromLocalStorage("customization") || undefined,
                }).unwrap(), {
                    error: {
                        402: t("error.paymentRequired"),
                        default: t("error.sessionCreation"),
                    }
                }, (newSession) => {
                    if (newSession) {
                        Routes.goNavigate(`${newSession.id}`);
                    } else {
                        error(t("error.sessionCreation"));
                    }
                });
            },
            confirmation: true,
            visible: (datum: Session) => SessionStateMap[datum.state] === SessionSimpleState.ABORTED,
            tooltip: <Translate i18nKey="acquisitions.table.actions.retrytip"/>,
        },
        {
            label: <><i className="fal fa-file-pdf action-icon"/> <Translate i18nKey="acquisitions.table.actions.pdf"/></>,
            items: (datum) => {
                const locales = getCompatibleLocales(datum.pdfTemplateVersion);
                return locales.map((locale) => ({
                    label: <Translate i18nKey={`locale.${locale}`}/>,
                    value: locale,
                }))
            },
            handler: async (datum: Session, locale) => {
                await tryCall(triggerGetReportUrl({
                    sessionId: datum.id,
                    locale: locale || Locale.EN,
                }).unwrap(), {error: t("error.pdfGeneration")}, (reportUrl) => {
                    if (reportUrl) {
                        createLinkToDownload(`Report_Session_${datum.id}_${locale || Locale.EN}.pdf`, reportUrl);
                    } else {
                        error(t("error.pdfGeneration"));
                    }
                });
            },
            visible: (datum: Session) => SessionStateMap[datum.state] === SessionSimpleState.COMPLETED,
            tooltip: <Translate i18nKey="acquisitions.table.actions.pdftip"/>,
        },
        {
            label: <><i className="fal fa-arrow-to-bottom action-icon"/> <Translate i18nKey="acquisitions.table.actions.pcap"/></>,
            handler: async (datum: Session) => {
                await tryCall(
                    triggerGetArchiveLink(datum.id).unwrap(),
                    {error: t("error.archiveGeneration")},
                    async (archiveUrl) => {
                        if (archiveUrl) {
                            if (await confirmBox({
                                message: t("acquisitions.table.actions.pcapAlert", {link: archiveUrl}),
                                yesText: `<i class="fa fa-download"></i> <span>${t("acquisitions.table.actions.pcapAlertButtonYes")}</span>`,
                                noText: `<i class="fa fa-times"></i> <span>${t("acquisitions.table.actions.pcapAlertButtonNo")}</span>`,
                                size: "large",
                            })) {
                                createLinkToDownload(`session_${datum.id}`, archiveUrl);
                            }
                        } else {
                            error(t("error.archiveGeneration"));
                        }
                    }
                )
            },
            visible: (datum: Session) => SessionStateMap[datum.state] === SessionSimpleState.COMPLETED,
            tooltip: <Translate i18nKey="acquisitions.table.actions.pcaptip"/>,
        },
        {
            label: <><i className="fal fa-trash action-icon"/> <Translate i18nKey="acquisitions.table.actions.del"/></>,
            handler: async (datum: Session) => {
                await tryCall(
                    triggerDeleteSession(datum.id).unwrap(),
                    { ok: t("admin.sessions.msg.del"), error: t("error.deleteSession")},
                );
            },
            confirmation: true,
            visible: (datum: Session) => SessionFinalStates.indexOf(datum.state) !== -1,
            tooltip: <Translate i18nKey="acquisitions.table.actions.deltip"/>,
        },
    ];

    const globalActions: KTableGlobalActionType<Session>[] = [
        {
            bulk: false,
            handler: async () => {refetch()},
            label: <i className="fal fa-sync action-icon"/>,
        },
    ];

    function renderSessionName(datum: object): React.ReactNode {
        const session = datum as Session;
        if (session.frameUrl && isAcquiring(session)) {
            return (
                <a href="/" onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {e.preventDefault(); onSetSession(session)}}>{session.name}</a>
            );
        } else {
            return session.name;
        }
    }

    function renderElapsed(datum: object): React.ReactNode {
        const session = datum as Session;
        if (session.start) {
            const elapsed = moment.duration(moment(new Date()).diff(moment(session.start)));
            return `${formatNumber(Math.floor(elapsed.asHours()))}:${formatNumber(elapsed.minutes())}:${formatNumber(elapsed.seconds())}`;
        } else {
            return "";
        }
    }

    function renderSessionState(datum: object): React.ReactNode {
        const session = datum as Session;
        return <Translate i18nKey={`acquisitions.stateValues.${SessionStateMap[session.state]}`}/>
    }

    return <> {(reduced && sessions && sessions.items.length === 0)
        ? null : (
            <KCard header={<Translate i18nKey={`acquisitions.title${reduced ? "LastFive" : "Complete"}`}/>}>
                {sessions ? (
                    <Row style={{marginTop: 0}}>
                        <Col>
                            <SessionTable
                                data={sessions}
                                header={{
                                    globalActions,
                                    filters: reduced ? undefined : [
                                        new KTableFreeTextFilter("name", t("acquisitions.table.name"), false),
                                        new KTableFixedSelectionFilter("state", t("acquisitions.table.state"), Object.keys(SimpleSessionStateMap).map((key) => ({
                                            label: key,
                                            value: SimpleSessionStateMap[key].join(","),
                                        })))
                                    ],
                                    columnSelector: !reduced,
                                }}
                                body={{
                                    checkboxes: false,
                                    columns: [
                                        {type: KTableColumnType.GENERIC, render: renderSessionName, label: t("acquisitions.table.name"), colSpan: 3, alignment: "left", sortField: !reduced ? "name" : undefined},
                                        {type: KTableColumnType.DATETIME, field: "created", label: t("acquisitions.table.created"), colSpan: 2, justDate: true, sortable: !reduced},
                                        {type: KTableColumnType.GENERIC, render: renderSessionState, label: t("acquisitions.table.state"), colSpan: 3, alignment: "right", sortField: !reduced ? "state" : undefined},
                                        {type: KTableColumnType.ACTIONS, actions, colSpan: 4, alignment: "right"},
                                    ],
                                }}
                                footer={{
                                    pager: {
                                        hide: reduced,
                                    },
                                }}
                            />
                        </Col>
                    </Row>
                ) : (
                    <Row style={{marginTop: 40, marginBottom: 30}}>
                        {/*<Col className="text-center">*/}
                        {/*<WASpinner size={200} variant="light"/>*/}
                        {/*</Col>*/}
                        <KTableLoader/>
                    </Row>
                )}
            </KCard>
        )}
        <KModal show={!forceHide && !!statedSessions && statedSessions.items.length > 0} size={KModalSize.lg} variant={KModalVariant.warning} header={<Translate i18nKey={"acquisitions.warnTitle"}/> } onHide={() => setForceHide(true)} backdrop={KModalBackdrop.static}>
            <p className="medium"><DangerHtml html={t("acquisitions.warnDescription")}/></p>
            <div className="small">
                <StartedSessionsTable
                    data={statedSessions}
                    header={{
                        columnSelector: false,
                    }}
                    footer={{
                        pager: {
                            hide: true,
                        },
                    }}
                    body={{
                        checkboxes: false,
                        columns: [
                            {type: KTableColumnType.FIELD, field: "name", sortable: false, label: t("acquisitions.table.name"), colSpan: 3, alignment: "left"},
                            {type: KTableColumnType.DATETIME, field: "created", sortable: false, label: t("acquisitions.table.created"), colSpan: 2, justDate: true},
                            {type: KTableColumnType.GENERIC, render: renderElapsed, sortable: false, label: t("admin.sessions.table.elapsed"), colSpan: 1, alignment: "center"},
                            {type: KTableColumnType.FIELD, field: "consumedCredits", sortable: false, label: t("admin.sessions.table.consumedCredits"), colSpan: 1, alignment: "right"},
                            {type: KTableColumnType.ACTIONS, colSpan: 4, actions: [
                                {
                                    label: <><i className="fal fa-arrow-right-to-bracket action-icon"/> <Translate i18nKey="acquisitions.table.actions.enter"/></>,
                                    handler: async (datum) => {
                                        $(".kapp").removeClass("blur");
                                        onSetSession(datum);
                                        setForceHide(true);
                                    },
                                    tooltip: <Translate i18nKey="acquisitions.table.actions.entertip"/>,
                                },
                                {
                                    label: <><i className="fal fa-times action-icon"/> <Translate i18nKey="acquisitions.table.actions.abort"/></>,
                                    handler: async (datum) => {
                                        await tryCall(triggerAbortSession(datum.id).unwrap(), {error: t("error.aborting")});
                                    },
                                    confirmation: true,
                                    visible: (datum: Session) => SessionStateMap[datum.state] === SessionSimpleState.INITIALIZING || SessionStateMap[datum.state] === SessionSimpleState.READY || (SessionStateMap[datum.state] === SessionSimpleState.STARTED && datum.consumedCredits <= 1),
                                    tooltip: <Translate i18nKey="acquisitions.table.actions.aborttip"/>,
                                },
                            ]},
                        ],
                    }}
                />
            </div>
        </KModal>
    </>;
};
